import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Users, Search, Mail, Shield, Ban, CheckCircle, X, Eye,
    Phone, MapPin, Calendar, ShoppingCart, Store, UserCheck,
    AlertTriangle, Loader2, RefreshCw, Crown, Star, Info, Trash2
} from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import DataTable from '../components/dashboard/DataTable';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth, API } from '../App';
import axios from 'axios';

const AdminUsers = () => {
    const { token } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [roleFilter, setRoleFilter] = useState('all');
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        if (token) fetchUsers();
    }, [token]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            toast.error('Failed to load users');
        }
        setLoading(false);
    };

    const changeUserRole = async (userId, newRole) => {
        setActionLoading(true);
        try {
            await axios.put(`${API}/api/admin/users/${userId}/role`,
                { role: newRole },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Role updated to ${newRole}`);
            // Update local state
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
            if (selectedUser?.id === userId) {
                setSelectedUser(prev => ({ ...prev, role: newRole }));
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update role');
        }
        setActionLoading(false);
    };

    const toggleUserStatus = async (userId, newStatus) => {
        setActionLoading(true);
        try {
            await axios.put(`${API}/api/admin/users/${userId}/status`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`User ${newStatus === 'banned' ? 'banned' : 'activated'} successfully`);
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: newStatus } : u));
            if (selectedUser?.id === userId) {
                setSelectedUser(prev => ({ ...prev, status: newStatus }));
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update status');
        }
        setActionLoading(false);
    };

    const deleteUser = async (userId) => {
        if (!window.confirm("Are you ABSOLUTELY sure you want to delete this user? This action cannot be undone.")) return;
        
        setActionLoading(true);
        try {
            await axios.delete(`${API}/api/admin/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("User deleted successfully!");
            setUsers(prev => prev.filter(u => u.id !== userId));
            setSelectedUser(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to delete user");
        }
        setActionLoading(false);
    };

    const filteredUsers = roleFilter === 'all' ? users : users.filter(u => u.role === roleFilter);

    const roleIcons = {
        customer: Users,
        vendor: Store,
        admin: Shield,
    };
    const roleColors = {
        customer: 'bg-blue-100 text-blue-700',
        vendor: 'bg-purple-100 text-purple-700',
        admin: 'bg-orange-100 text-orange-700',
    };
    const statusColors = {
        active: 'bg-green-100 text-green-700',
        inactive: 'bg-gray-100 text-gray-700',
        banned: 'bg-red-100 text-red-700',
        pending: 'bg-yellow-100 text-yellow-700',
        approved: 'bg-green-100 text-green-700',
        rejected: 'bg-red-100 text-red-700',
    };

    const columns = [
        {
            key: 'name',
            label: 'User',
            render: (value, row) => (
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${row.role === 'admin' ? 'bg-orange-100' : row.role === 'vendor' ? 'bg-purple-100' : 'bg-primary/10'
                        }`}>
                        <span className={`font-semibold ${row.role === 'admin' ? 'text-orange-600' : row.role === 'vendor' ? 'text-purple-600' : 'text-primary'
                            }`}>{(value || '?')[0].toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                        <p className="font-medium truncate flex items-center gap-1.5">
                            {value || 'Unknown'}
                            {row.role === 'admin' && <Crown className="w-3.5 h-3.5 text-orange-500" />}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{row.email}</p>
                    </div>
                </div>
            )
        },
        {
            key: 'role',
            label: 'Role',
            render: (value) => <Badge className={roleColors[value] || 'bg-gray-100 text-gray-700'}>{value}</Badge>
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => {
                const displayStatus = value || 'active';
                return <Badge className={statusColors[displayStatus] || 'bg-gray-100 text-gray-700'}>{displayStatus}</Badge>;
            }
        },
        {
            key: 'order_count',
            label: 'Orders',
            render: (value) => <span className="font-medium">{value || 0}</span>
        },
        {
            key: 'phone',
            label: 'Phone',
            render: (value) => <span className="text-sm text-muted-foreground">{value || '—'}</span>
        },
        {
            key: 'created_at',
            label: 'Joined',
            render: (value) => (
                <span className="text-sm text-muted-foreground">
                    {value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                </span>
            )
        }
    ];

    const tableActions = [
        { label: 'View Details', icon: Eye, onClick: (row) => setSelectedUser(row) },
        {
            label: (row) => (row.status === 'banned' ? 'Unban User' : 'Ban User'),
            icon: Ban,
            variant: 'destructive',
            onClick: (row) => toggleUserStatus(row.id, row.status === 'banned' ? 'active' : 'banned'),
            hidden: (row) => row.role === 'admin'
        }
    ];

    const roleCounts = {
        all: users.length,
        customer: users.filter(u => u.role === 'customer').length,
        vendor: users.filter(u => u.role === 'vendor').length,
        admin: users.filter(u => u.role === 'admin').length,
    };

    const filterCards = [
        { key: 'all', label: 'All Users', icon: Users, color: 'text-primary', bg: 'bg-primary/10' },
        { key: 'customer', label: 'Customers', icon: UserCheck, color: 'text-blue-600', bg: 'bg-blue-100' },
        { key: 'vendor', label: 'Vendors', icon: Store, color: 'text-purple-600', bg: 'bg-purple-100' },
        { key: 'admin', label: 'Admins', icon: Shield, color: 'text-orange-600', bg: 'bg-orange-100' },
    ];

    // Detail row for user modal
    const DetailRow = ({ icon: Icon, label, value, iconColor = 'text-muted-foreground' }) => (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
            <div className={`mt-0.5 ${iconColor}`}>
                <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="font-medium text-sm truncate">{value || '—'}</p>
            </div>
        </div>
    );

    return (
        <DashboardLayout userRole="admin">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                                <Users className="w-5 h-5 text-primary" />
                            </div>
                            User Management
                        </h1>
                        <p className="text-muted-foreground mt-1 ml-13">Manage all platform users, roles, and permissions</p>
                    </div>
                    <Button variant="outline" onClick={fetchUsers} disabled={loading}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>

                {/* Filter Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {filterCards.map(({ key, label, icon: Icon, color, bg }) => {
                        const isActive = roleFilter === key;
                        return (
                            <motion.button
                                key={key}
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setRoleFilter(key)}
                                className={`bg-card rounded-xl border p-4 text-left transition-all ${isActive ? 'ring-2 ring-primary shadow-md' : 'hover:border-primary/50 hover:shadow-sm'
                                    }`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className={`w-9 h-9 ${bg} rounded-lg flex items-center justify-center`}>
                                        <Icon className={`w-4.5 h-4.5 ${color}`} />
                                    </div>
                                    {isActive && <div className="w-2 h-2 rounded-full bg-primary" />}
                                </div>
                                <p className="text-2xl font-bold">{roleCounts[key]}</p>
                                <p className="text-sm text-muted-foreground mt-0.5">{label}</p>
                            </motion.button>
                        );
                    })}
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-48 gap-3 bg-card rounded-xl border">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">Loading users...</p>
                    </div>
                ) : (
                    <DataTable
                        columns={columns}
                        data={filteredUsers}
                        actions={tableActions}
                        searchPlaceholder="Search by name, email, phone..."
                        onRowClick={(row) => setSelectedUser(row)}
                    />
                )}

                {/* User Detail Modal */}
                <AnimatePresence>
                    {selectedUser && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                            onClick={() => setSelectedUser(null)}
                        >
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                className="bg-card rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Modal Header */}
                                <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-card z-10 rounded-t-2xl">
                                    <h2 className="text-xl font-bold">User Details</h2>
                                    <Button variant="ghost" size="icon" onClick={() => setSelectedUser(null)}>
                                        <X className="w-5 h-5" />
                                    </Button>
                                </div>

                                <div className="p-6 space-y-6">
                                    {/* User Avatar & Info */}
                                    <div className="flex items-center gap-4">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${selectedUser.role === 'admin' ? 'bg-gradient-to-br from-orange-400 to-orange-600' :
                                                selectedUser.role === 'vendor' ? 'bg-gradient-to-br from-purple-400 to-purple-600' :
                                                    'bg-gradient-to-br from-blue-400 to-blue-600'
                                            }`}>
                                            <span className="text-2xl font-bold text-white">
                                                {(selectedUser.name || '?')[0].toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-semibold flex items-center gap-2">
                                                {selectedUser.name || 'Unknown User'}
                                                {selectedUser.role === 'admin' && <Crown className="w-4 h-4 text-orange-500" />}
                                            </h3>
                                            <p className="text-muted-foreground text-sm">{selectedUser.email}</p>
                                            <div className="flex items-center gap-2 mt-1.5">
                                                <Badge className={roleColors[selectedUser.role] || 'bg-gray-100'}>{selectedUser.role}</Badge>
                                                <Badge className={statusColors[selectedUser.status || 'active'] || 'bg-gray-100'}>
                                                    {selectedUser.status || 'active'}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Info Grid */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <DetailRow icon={Mail} label="Email" value={selectedUser.email} iconColor="text-blue-500" />
                                        <DetailRow icon={Phone} label="Phone" value={selectedUser.phone} iconColor="text-green-500" />
                                        <DetailRow icon={ShoppingCart} label="Orders" value={selectedUser.order_count || 0} iconColor="text-purple-500" />
                                        <DetailRow icon={Calendar} label="Joined" value={
                                            selectedUser.created_at
                                                ? new Date(selectedUser.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                                                : '—'
                                        } iconColor="text-orange-500" />
                                    </div>

                                    {/* Vendor-Specific Details */}
                                    {selectedUser.role === 'vendor' && (
                                        <div className="space-y-3">
                                            <h4 className="font-semibold text-sm flex items-center gap-2">
                                                <Store className="w-4 h-4 text-purple-500" />
                                                Business Details
                                            </h4>
                                            <div className="grid grid-cols-2 gap-3">
                                                <DetailRow icon={Store} label="Business Name" value={selectedUser.business_name} iconColor="text-purple-500" />
                                                <DetailRow icon={Star} label="Business Type" value={selectedUser.business_type} iconColor="text-yellow-500" />
                                                <DetailRow icon={MapPin} label="City" value={selectedUser.city} iconColor="text-red-500" />
                                                <DetailRow icon={MapPin} label="State" value={selectedUser.state} iconColor="text-red-400" />
                                            </div>
                                            {selectedUser.business_address && (
                                                <DetailRow icon={MapPin} label="Business Address" value={selectedUser.business_address} iconColor="text-red-500" />
                                            )}
                                            {/* GST & PAN masked info if available */}
                                            <div className="grid grid-cols-2 gap-3">
                                                {selectedUser.gst_number && (
                                                    <DetailRow icon={Info} label="GST Number" value={selectedUser.gst_number} iconColor="text-indigo-500" />
                                                )}
                                                {selectedUser.pan_number && (
                                                    <DetailRow icon={Info} label="PAN Number" value={selectedUser.pan_number} iconColor="text-indigo-500" />
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Role Management */}
                                    <div className="space-y-3 pt-2">
                                        <Label className="font-semibold flex items-center gap-2">
                                            <Shield className="w-4 h-4 text-primary" />
                                            Change Role
                                        </Label>

                                        {selectedUser.role === 'vendor' ? (
                                            <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                                <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                                <p className="text-sm text-yellow-800">
                                                    Vendor roles cannot be changed here. Vendors require verified business details and documents. Use <span className="font-semibold">Vendor Management</span> instead.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="flex gap-2">
                                                <Button
                                                    variant={selectedUser.role === 'customer' ? 'default' : 'outline'}
                                                    size="sm"
                                                    onClick={() => changeUserRole(selectedUser.id, 'customer')}
                                                    disabled={selectedUser.role === 'customer' || actionLoading}
                                                    className="flex-1"
                                                >
                                                    <Users className="w-4 h-4 mr-1.5" />
                                                    Customer
                                                </Button>
                                                <Button
                                                    variant={selectedUser.role === 'admin' ? 'default' : 'outline'}
                                                    size="sm"
                                                    onClick={() => changeUserRole(selectedUser.id, 'admin')}
                                                    disabled={selectedUser.role === 'admin' || actionLoading}
                                                    className="flex-1"
                                                >
                                                    <Shield className="w-4 h-4 mr-1.5" />
                                                    Admin
                                                </Button>
                                            </div>
                                        )}

                                        {/* Info about vendor */}
                                        {selectedUser.role !== 'vendor' && (
                                            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                                                <Info className="w-3 h-3" />
                                                To become a vendor, users must register through the vendor onboarding process with complete business details and documents.
                                            </p>
                                        )}
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex gap-3 pt-4 border-t">
                                        {selectedUser.role !== 'admin' && (
                                            <Button
                                                variant="outline"
                                                className={selectedUser.status === 'banned' ? 'text-green-600 hover:bg-green-50' : 'text-red-600 hover:bg-red-50'}
                                                onClick={() => {
                                                    const newStatus = selectedUser.status === 'banned' ? 'active' : 'banned';
                                                    toggleUserStatus(selectedUser.id, newStatus);
                                                }}
                                                disabled={actionLoading}
                                            >
                                                {actionLoading ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : selectedUser.status === 'banned' ? (
                                                    <CheckCircle className="w-4 h-4 mr-2" />
                                                ) : (
                                                    <Ban className="w-4 h-4 mr-2" />
                                                )}
                                                {selectedUser.status === 'banned' ? 'Unban User' : 'Ban User'}
                                            </Button>
                                        )}
                                        {selectedUser.role !== 'admin' && (
                                            <Button variant="outline" className="text-red-700 hover:bg-red-50 hover:text-red-800 border-red-200" onClick={() => deleteUser(selectedUser.id)} disabled={actionLoading}>
                                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                                            </Button>
                                        )}
                                        <Button variant="outline" className="flex-1" onClick={() => setSelectedUser(null)}>
                                            Close
                                        </Button>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </DashboardLayout>
    );
};

export default AdminUsers;
